import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { getMenuAuth } from "src/apis/authApi";
import useActionbarAuthStore from "src/store/useActionbarAuthStore";

import { downloadExcel, downloadSettingExcel } from "src/utils/blob/blobUtil";

import { deleteEQM2020_R01, disableEQM2020_R01, downloadExcel_EQM2020_R01, getEQM2020_R01AllCount, getEQM2020_R01List, patchEQM2020_R01, postEQM2020_R01 } from "./Api_EQM2020_R01";
import { defaultEQM2020_R01Filter, EQM2020_R01Filter, useEQM2020_R01Store } from "./Store_EQM2020_R01";
import { useLoadingSpinnerStore } from "src/store/util/loadingSpinnerUtil";
import { defaultEQM2020_R01Dto, EQM2020_R01Dto } from "./Dto_EQM2020_R01";
import useFetchActionbarAuth from "src/hooks/useFetchActionbarAuth";
import { showSuccess, ShowSuccessFetch, showWarning } from "src/utils/toast/toastUtil";
import { useCommonCodeStore } from "src/store/useCommonCodeStore";
import { validateString } from "src/utils/string/stringUtil";
import { refetchUpdateList_Excelgrid } from "src/utils/axios/reFetch";
import { AgGridReact } from "ag-grid-react";
import { fetchDataSetHandler, fetchDataSetHandler_refetch, fetchFilterConfig, fetchFilterConfig_refetch, InsertRowHandler, UpdateRowHandler } from "src/utils/grid/GridHandlerContent";
import { useCommonCodeValidate } from "src/utils/commonCode/CommonCodeValidateContext";
import { buildCommonCodeRequirements, EQM2020_R01_ROW_VALIDATE } from "src/utils/commonCode/commonCodeRequirementConfig";


interface UserContentProps {
	gridRef: React.RefObject<AgGridReact<EQM2020_R01Dto> | null>;
	setMessage: (message: string) => void;  
}	

type FetchMessageMode = 'show' | 'none';

interface FetchOptions {
	messageMode?: FetchMessageMode;
}

export const Content_EQM2020_R01 = (props: UserContentProps) => {

	const { 
		gridRef,
		setMessage,
	} = props;

	const {
		EQM2020_R01Filter, setEQM2020_R01Filter,
		EQM2020_R01PastFilter, setEQM2020_R01PastFilter,
		EQM2020_R01List, setEQM2020_R01List, insertEQM2020_R01List, removeEQM2020_R01List,
		deletedEQM2020_R01List, addDeletedEQM2020_R01List, removeDeletedEQM2020_R01List,
		selectedEQM2020_R01List, setSelectedEQM2020_R01List, removeSelectedEQM2020_R01List,
		originalEQM2020_R01List, setOriginalEQM2020_R01List,
		allCount, setAllCount,
		initData,
		setScrollTop,
	} = useEQM2020_R01Store();

	const { inspectionStatusList, inspectionValueList, yesNoList, } = useCommonCodeStore();
	const { validateCommonCodeLists } = useCommonCodeValidate();
	const { setLoadingSpinner } = useLoadingSpinnerStore();

	const [clickable, setClickable] = useState(true);
    useActionbarAuthStore((state) => state.setAuth);
// 권한 정보 로드
	useFetchActionbarAuth("EQM2020_R01");

	// 버튼 비활성화 상태 관리
	const [buttonDisabled, setButtonDisabled] = useState({
		fetch: false,      // 조회: 기본 활성화
		create: false,     // 생성: 기본 활성화
		delete: true,      // 삭제: 기본 비활성화
		save: true,        // 저장: 기본 비활성화
		print: true,       // 출력: 기본 비활성화
		excel: true,       // 엑셀: 기본 비활성화
		init: false        // 초기화: 항상 활성화
	});

	// 선택된 사용자와 삭제된 사용자가 변경될 때 버튼 상태 업데이트
	useEffect(() => {
		setButtonDisabled(prev => ({
			...prev,
			delete: deletedEQM2020_R01List.length === 0 && selectedEQM2020_R01List.length === 0,
			save: EQM2020_R01List.length === 0,
			print: EQM2020_R01List.length === 0,
			excel: EQM2020_R01List.length === 0
		}));
	}, [selectedEQM2020_R01List, deletedEQM2020_R01List, EQM2020_R01List]);
	
	
	const handleExcelClick = useCallback(async () => {
		setLoadingSpinner(true);

		const { isDefaultFilter, listLength } = fetchFilterConfig(
			EQM2020_R01Filter,
			EQM2020_R01PastFilter,
			defaultEQM2020_R01Filter,
			EQM2020_R01List.length
		);

		const response = await downloadExcel_EQM2020_R01(
			isDefaultFilter? '' : 'search',
			getParams(EQM2020_R01Filter),
			listLength
		);
		downloadSettingExcel({ responseData: response.data.data, fileName: '설비 점검코드 등록-excel.xlsx' });

		setLoadingSpinner(false);
	}, [EQM2020_R01Filter, EQM2020_R01List.length, EQM2020_R01PastFilter, setLoadingSpinner])

	const validateInsertData = (data: EQM2020_R01Dto, currentRowIndex: number) => {
		if(!validateString(data.eqmType)) {
			showWarning('설비유형을 입력해주세요.'); return false;
		}
		if(!validateString(data.inspectNm)) {
			showWarning('점검명을 입력해주세요.'); return false;
		}
		if(!validateString(data.inspectMethod)) {
			showWarning('점검방법을 입력해주세요.'); return false;
		}
		if(!validateString(data.inspectType)) {
			showWarning('점검유형을 입력해주세요.'); return false;
		}
		if(!validateString(data.inspectValue)) {
			showWarning('결과유형을 입력해주세요.'); return false;
		}
		if(!validateString(data.inspectUse)) {
			showWarning('사용여부를 입력해주세요.'); return false;
		}

		// 1. 삭제하기로 표시된 사용자들을 먼저 제외
		const activeUsers = EQM2020_R01List.filter(user => !deletedEQM2020_R01List.includes(user.valueIndex.toString()));
		// 현재 검증 중인 행의 인덱스를 제외한 나머지 행들
		const otherUsers = activeUsers.filter((_, index) => index !== currentRowIndex);
		
		if (validateString(data.inspectCd)
			&& otherUsers.some(user => (user.inspectCd === data.inspectCd && user.eqmType === data.eqmType))) {
			showWarning("중복된 점검코드가 있습니다.\n점검코드: " + data.inspectCd);
			return false;
		}

		
		return true;
	}

	const getParams = (filter: EQM2020_R01Filter) => {
		const params: Partial<EQM2020_R01Filter> = {
			eqmType: filter.eqmType || undefined,
			inspectType: filter.inspectType || undefined,
			inspectUse: filter.inspectUse || undefined,
		};
		return params;
	}

	const renewalAllCount = useCallback(async () => {
		const allCountResponse = await getEQM2020_R01AllCount(getParams(EQM2020_R01Filter));
		setAllCount(allCountResponse.data.data);
		return allCountResponse.data.data;
	}, [EQM2020_R01Filter, setAllCount]);

	const executeDataFetch = useCallback(async (
		fetchType: 'search' | 'refetch',
		options?: FetchOptions
	) => {
		const messageMode = options?.messageMode ?? 'show';
		setLoadingSpinner(true);
		if (fetchType === 'search') {
			setScrollTop(null);
		}
// 저장 후 재조회 시에는 목록을 비우기.
		if (fetchType === 'refetch') {
			setEQM2020_R01List([]);
		}
	
		const responseData = await getEQM2020_R01List(
			'search',
			EQM2020_R01Filter,
			EQM2020_R01List.length ?? 0
		);
		
		let newList = [...responseData.data.data ] as EQM2020_R01Dto[];
		setEQM2020_R01List(newList);

		const processAllCount = await renewalAllCount();
		setOriginalEQM2020_R01List(newList);
		setSelectedEQM2020_R01List([]);
		removeDeletedEQM2020_R01List(deletedEQM2020_R01List);
		gridRef.current?.api.ensureIndexVisible(0, 'top');
	
		if (newList.length === 0) {
			// insertEQM2020_R01List([{ ...defaultEQM2020_R01Dto, inserted: true }], 0);
			if (messageMode === 'show') {
				showWarning('데이터가 없습니다.');
			}
		}else{
			setEQM2020_R01PastFilter(EQM2020_R01Filter);
			if (messageMode === 'show') {
				ShowSuccessFetch(setMessage, `조회가 완료되었습니다. (총 ${newList.length}/${processAllCount}건)`);
			}
		}
	
		setLoadingSpinner(false);
		if (messageMode === 'show') {
			setTimeout(() => setMessage(""), 5000);
		}
	}, [
		EQM2020_R01Filter,  EQM2020_R01PastFilter, EQM2020_R01List, deletedEQM2020_R01List,
		gridRef, setMessage, setLoadingSpinner,
		setOriginalEQM2020_R01List, setSelectedEQM2020_R01List, insertEQM2020_R01List, setScrollTop,
	]);
	
	/**
	 * 사용자 목록 조회 함수
	 * 검색 조건에 맞는 사용자 목록을 가져옴
	 */
	const fetchData = useCallback(async () => {
		executeDataFetch('search');
	}, [executeDataFetch])
	
	
	const defaultEQM2020_R01Row = useCallback(() => {

		const commonResult = validateCommonCodeLists(
			buildCommonCodeRequirements(EQM2020_R01_ROW_VALIDATE, {
				inspectionStatusList,
				inspectionValueList,
				yesNoList,
			}),
		);
		if (!commonResult.valid) { return { ...defaultEQM2020_R01Dto }; }

		return {
			...defaultEQM2020_R01Dto,
			inspectType: inspectionStatusList[0].minorCd,
			inspectValue : inspectionValueList[0].minorCd,
			inspectUse : yesNoList[0].minorCd,
			inserted: true,
		}
	}, [defaultEQM2020_R01Dto, inspectionStatusList, inspectionValueList, yesNoList, validateCommonCodeLists]);

	const initContainerData = useCallback(() => {
		initData();
	}, [initData]);
	
	// 상단 '등록' 버튼 전용 핸들러
	const handleInsertClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
		InsertRowHandler({
			event: e,
			dataList: EQM2020_R01List,
			rowData: {...defaultEQM2020_R01Row(), valueIndex: -1},
			insertRow: insertEQM2020_R01List,
			defaultRowData: defaultEQM2020_R01Row(),
			setClickable
		});
	}, [EQM2020_R01List, defaultEQM2020_R01Row, insertEQM2020_R01List]);	
	
	const handleSaveClick = useCallback(async () => {

		// 신규 사용자 처리
		const insertedList: EQM2020_R01Dto[] = EQM2020_R01List.filter(row => 
			!deletedEQM2020_R01List.includes(row.valueIndex.toString()) && 
			row.inserted
		);

		// 수정된 사용자만 필터링
		const modifiedList = EQM2020_R01List.filter(row => 
			!deletedEQM2020_R01List.includes(row.valueIndex.toString()) && 
			row.isModified && !row.inserted
		);

		
        // 수정 사용자 유효성 검증
		let seenMenuSet = new Set<string>();
		let valid = true;
		modifiedList.forEach((value) => {
			let index = EQM2020_R01List.findIndex(user => user.valueIndex === value.valueIndex);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.valueIndex.toString());
		});
		if(!valid) { return; }
        // 신규 사용자 유효성 검증
		seenMenuSet = new Set<string>();
		valid = true;
		insertedList.forEach((value) => {
			let index = EQM2020_R01List.findIndex(user => user.valueIndex === value.valueIndex);
			if(!validateInsertData(value, index)) valid = false;
			seenMenuSet.add(value.valueIndex.toString());
		});
		if(!valid) { return; }

		const deleteStringList = deletedEQM2020_R01List.map(item => item.toString());
		const deleteTargetList = EQM2020_R01List.filter(item => deleteStringList.includes(item.valueIndex.toString()));

		const [insertedResponse, modifiedResponse, deleteResponse] = await Promise.all([
			insertedList.length > 0 ? postEQM2020_R01(insertedList) : Promise.resolve(),
			modifiedList.length > 0 ? patchEQM2020_R01(modifiedList) : Promise.resolve(),
			deletedEQM2020_R01List.length > 0 ? Promise.all(deleteTargetList.map(value => deleteEQM2020_R01(value))) : Promise.resolve()
		]);

		if(insertedResponse?.data.successFlag || modifiedResponse?.data.successFlag) {
			showSuccess('설비 점검코드 저장이 완료되었습니다.');
			setMessage('설비 점검코드 저장이 완료되었습니다.');
		} else if(deleteResponse) {
			showSuccess('설비 점검코드 삭제가 완료되었습니다.');
			setMessage('설비 점검코드 삭제가 완료되었습니다.');
		}


		setTimeout(() => { setMessage(''); }, 5000);
		await executeDataFetch('refetch', { messageMode: 'none' });

	}, [EQM2020_R01List, deletedEQM2020_R01List, executeDataFetch, validateInsertData, setMessage]);
	
	/**
	 * 삭제 핸들러
	 * 삭제 표시된 행이 있으면 복구, 없으면 선택된 행을 삭제 표시
	 */
	const handleDeleteClick = useCallback(() => {
		if (deletedEQM2020_R01List.length > 0) {
			// 삭제 표시된 행이 있으면 전체 복구
			removeDeletedEQM2020_R01List(deletedEQM2020_R01List);
			removeSelectedEQM2020_R01List();
			setEQM2020_R01List([...EQM2020_R01List]);
		} else if (selectedEQM2020_R01List.length > 0) {
			// 삭제 표시된 행이 없으면 선택된 행만 삭제 표시
			addDeletedEQM2020_R01List(selectedEQM2020_R01List);
			removeSelectedEQM2020_R01List(selectedEQM2020_R01List);
		}
	}, [selectedEQM2020_R01List, deletedEQM2020_R01List, addDeletedEQM2020_R01List, removeDeletedEQM2020_R01List, removeSelectedEQM2020_R01List, setEQM2020_R01List, EQM2020_R01List]);

	return{
		clickable, setClickable,
		handleExcelClick,
		fetchData,
		defaultEQM2020_R01Row,
		initContainerData,
		handleInsertClick,
		handleSaveClick,
		handleDeleteClick,
		buttonDisabled,
	}
}
